import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import FlashCard from './Flashcard';
import { useTriviaQuestions } from '../../hooks/useGameQuestions';
import { integratedLanguageService } from '../../services/integratedLanguageService';
import { loadLanguage } from '../../lib/i18n';
import { Flag, Check, X } from 'lucide-react';
import ReportModal from '../modals/ReportModal';


import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../lib/SettingsContext';

type SupportedLanguage = 'en' | 'es' | 'fr' | 'hi' | 'de';

interface TriviaGameCardProps {
  isVisible: boolean;
  onComplete?: (gameResultCandidate?: string) => void;
  spinnerDimension?: number;
  winnerName?: string;
  language?: SupportedLanguage;
}

const TriviaGameCard: React.FC<TriviaGameCardProps> = ({ 
  isVisible, 
  onComplete,
  winnerName = "Winner",
  language
}) => {
  // Animation sequence state - same as FlashcardDemo
  const [currentSet, setCurrentSet] = useState(1); // 1 = colorful->question, 2 = question->answer
  const [isFlipped, setIsFlipped] = useState(false);
  const [set2IsFlipped, setSet2IsFlipped] = useState(false); // Separate state for set 2

  // Game state
  const [currentQuestion, setCurrentQuestion] = useState<{
    id: string;
    question: string;
    A: string;
    B: string;
    C: string;
    D: string;
    Answer: string;
    funFact: string;
    category: string;
  } | null>(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [isFadingIn, setIsFadingIn] = useState(false);
  const [selectedAction, setSelectedAction] = useState<'report' | null>(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const { t, i18n, ready } = useTranslation();
  const { user } = useAuth();
  const { audioEnabled } = useSettings();
  const [currentLanguage, setCurrentLanguage] = useState<SupportedLanguage>('en');
  const prevLanguageRef = React.useRef<SupportedLanguage | null>(null);

  // Use the new CDN-based question loading system
  const {
    error: questionError,
    getNextQuestion,
    initialize: initializeQuestions,
    forceReload,
    stats
  } = useTriviaQuestions(currentLanguage, user?.uid || null, isVisible);

  // Add refs for sounds
  const notSelectedAudioRef = React.useRef<HTMLAudioElement | null>(null);
  const rightAnswerAudioRef = React.useRef<HTMLAudioElement | null>(null);
  const wrongAnswerAudioRef = React.useRef<HTMLAudioElement | null>(null);

  // Initialize audio elements with error handling
  const initializeAudio = (ref: React.MutableRefObject<HTMLAudioElement | null>, src: string) => {
    if (!ref.current) {
      try {
        ref.current = new Audio(src);
        ref.current.preload = 'auto';
        
        // Add error handling for audio loading
        ref.current.addEventListener('error', (e) => {
          // Don't log 416 errors (range not satisfiable) as they're not critical
          const target = e.target as HTMLAudioElement;
          if (target.error && target.error.code === 4) {
            console.debug(`Audio range request not supported for ${src} (416 error - not critical)`);
          } else {
            console.warn(`Audio loading error for ${src}:`, e);
          }
        });
        
        // Add load event to confirm successful loading
        ref.current.addEventListener('canplaythrough', () => {
          console.debug(`✅ Audio loaded successfully: ${src}`);
        });
        
      } catch (error) {
        console.warn(`Failed to initialize audio for ${src}:`, error);
      }
    }
    return ref.current;
  };

  // Auto-switch to second set after first flip completes - same as FlashcardDemo
  useEffect(() => {
    if (currentSet === 1 && isFlipped) {
      // Wait for flip animation to complete, then switch to second set
      const timer = setTimeout(() => {
        setCurrentSet(2);
        // Don't change isFlipped - keep it true so there's no visual change
        setSet2IsFlipped(false); // Set 2 starts unflipped (showing question)
      }, 650); // Slightly longer to ensure flip is complete
      
      return () => clearTimeout(timer);
    }
  }, [currentSet, isFlipped]);

  // Update window width on resize
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Set current language
  useEffect(() => {
    const lang = language || 'en';
    setCurrentLanguage(lang);
  }, [language]);

  // Handle language change
  useEffect(() => {
    const lang = language || 'en';
    if (prevLanguageRef.current && prevLanguageRef.current !== lang) {
      integratedLanguageService.changeLanguage(lang, 'trivia').catch(error => {
        console.error('Error changing language in TriviaGameCard:', error);
      });
    }
    prevLanguageRef.current = lang;
  }, [currentLanguage, language]);

  // Ensure language translations are loaded
  useEffect(() => {
    const lang = language || 'en';
    if (lang !== 'en') {
      loadLanguage(lang).catch(error => {
        console.error('Error loading language translations:', error);
      });
    }
  }, [language]);

  // Preload audio files when component mounts
  useEffect(() => {
    if (audioEnabled) {
      // Preload all audio files to avoid loading errors
      initializeAudio(notSelectedAudioRef, '/sounds/not-selected.mp3');
      initializeAudio(rightAnswerAudioRef, '/sounds/right-answer.mp3');
      initializeAudio(wrongAnswerAudioRef, '/sounds/wrong-answer.mp3');
    }
  }, [audioEnabled]);

  // Calculate card size with standard aspect ratio and responsive scaling
  const maxCardWidth = Math.min(window.innerWidth * 0.95, 400);
  const maxCardHeight = Math.min(window.innerHeight * 0.95, 400);
  let cardWidth = maxCardWidth;
  let cardHeight = cardWidth / 1.5;
  if (cardHeight > maxCardHeight) {
    cardHeight = maxCardHeight;
    cardWidth = cardHeight * 1.5;
  }
  if (windowWidth > 768) {
    cardWidth = cardWidth * 1.2;
    cardHeight = cardHeight * 1.2;
  }
  const fontSize = cardWidth * 0.045;

  // Handle card click - same logic as FlashcardDemo
  const handleCardClick = () => {
    if (currentSet === 1 && !isFlipped) {
      // First click: flip to question (will auto-switch to set 2 after animation)
      setIsFlipped(true);
    } else if (currentSet === 2 && !set2IsFlipped) {
      // Second click: flip to answer
      setSet2IsFlipped(true);
    }
    // No action when answer is showing - end of sequence
  };

  // Reset card state and fetch new question when it becomes visible
  useEffect(() => {
    if (isVisible) {
      // Reset animation sequence
      setCurrentSet(1);
      setIsFlipped(false);
      setSet2IsFlipped(false);
      setSelectedAnswer(null);
      setSelectedAction(null); // Reset action selection for new question
      setShowReportModal(false);
      
      setIsFadingOut(false);
      setIsFadingIn(true);
      fetchNewQuestion();
      
      // Complete fade-in after animation
      setTimeout(() => {
        setIsFadingIn(false);
      }, 300);
    }
  }, [isVisible, currentLanguage]);

  // Fetch a new random question using CDN system (no static fallback)
  const fetchNewQuestion = async () => {
    try {
      console.log('🔄 Fetching new question from CDN...');
      console.log('📊 Current stats:', stats);
      console.log('🔍 Manager state - error:', questionError);
      
      // If nothing is loaded yet, initialize or force reload
      if (!stats || stats.currentQuestions === 0) {
        await initializeQuestions();
      }
      
      let attempts = 0;
      const maxAttempts = 6;
      let picked: any = null;

      while (attempts < maxAttempts) {
        attempts++;
        console.log(`🔄 Attempt ${attempts} - calling getNextQuestion()...`);
        const q = getNextQuestion();
        console.log(`📝 Attempt ${attempts} got:`, q);
        
        // Check for CDN format (A, B, C, D properties with Answer)
        if (q && q.A && q.B && q.C && q.D && q.Answer && q.question) {
          picked = q;
          break;
        }
        // Also check for legacy format (options array with correctIndex)
        else if (q && Array.isArray(q.options) && q.options.length >= 4 && q.correctIndex !== undefined) {
          picked = q;
          break;
        }
        
        // If invalid/null, wait a bit and try to ensure next set is prepared
        if (attempts === 3) {
          await forceReload();
        }
        await new Promise(r => setTimeout(r, 250));
      }
      
      if (picked) {
        let newQuestion;
        
        // Handle CDN format (A, B, C, D properties with Answer)
        if (picked.A && picked.B && picked.C && picked.D && picked.Answer) {
          newQuestion = {
            id: String(picked.id),
            question: picked.question || '',
            A: picked.A || '',
            B: picked.B || '',
            C: picked.C || '',
            D: picked.D || '',
            Answer: picked.Answer || '',
            funFact: picked.funFact || '',
            category: picked.category || 'general'
          };
        }
        // Handle legacy format (options array with correctIndex)
        else if (Array.isArray(picked.options)) {
          newQuestion = {
            id: String(picked.id),
            question: picked.question || '',
            A: picked.options[0] || '',
            B: picked.options[1] || '',
            C: picked.options[2] || '',
            D: picked.options[3] || '',
            Answer: picked.options[picked.correctIndex] || '',
            funFact: picked.funFact || '',
            category: picked.category || 'general'
          };
        }
        
        console.log('✅ Setting new question:', newQuestion);
        setCurrentQuestion(newQuestion);
      } else {
        console.warn('⌛ Questions not ready yet; leaving card in loading state');
        setCurrentQuestion(null);
      }
    } catch (error) {
      console.error('❌ Error fetching question from CDN:', error);
      setCurrentQuestion(null);
    }
  };

  // Handle report button click
  const handleReportClick = () => {
    setShowReportModal(true);
    setSelectedAction(null); // Reset action selection when opening modal
  };

  // Handle report submission
  const handleReportSubmitted = () => {
    // Report submitted successfully
    // Set the selectedAction to 'report' to show the button as filled
    setSelectedAction('report');
    // You can add any additional logic here, like showing a success message
  };



  // Auto-fade out answer card after 8 seconds
  useEffect(() => {
    if (currentSet === 2 && set2IsFlipped) {
      const timer = setTimeout(() => {
        setIsFadingOut(true);
        setTimeout(() => {
          if (onComplete) {
            // Determine outcome based on whether user answered correctly
            const gameResultCandidate = selectedAnswer ? (isAnswerCorrect() ? '🟢' : '🔴') : '🔘';
            onComplete(gameResultCandidate);
          }
        }, 500);
      }, 8000);
      return () => {
        clearTimeout(timer);
      };
    }
  }, [currentSet, set2IsFlipped, onComplete]);

  // Function to determine if the selected answer is correct
  const isAnswerCorrect = () => {
    if (!currentQuestion || !selectedAnswer) return false;
    
    let selectedAnswerText = '';
    switch (selectedAnswer) {
      case 'A':
        selectedAnswerText = currentQuestion.A;
        break;
      case 'B':
        selectedAnswerText = currentQuestion.B;
        break;
      case 'C':
        selectedAnswerText = currentQuestion.C;
        break;
      case 'D':
        selectedAnswerText = currentQuestion.D;
        break;
      default:
        return false;
    }
    
    return selectedAnswerText.trim().toLowerCase() === currentQuestion.Answer.trim().toLowerCase();
  };

  // Play sound when answer card is shown
  useEffect(() => {
    if (currentSet === 2 && set2IsFlipped && selectedAnswer && audioEnabled) {
      const isCorrect = isAnswerCorrect();
      if (isCorrect) {
        const audio = initializeAudio(rightAnswerAudioRef, '/sounds/right-answer.mp3');
        if (audio) {
          audio.currentTime = 0;
          audio.play().catch(error => {
            console.error('Error playing right answer sound:', error);
          });
        }
      } else {
        const audio = initializeAudio(wrongAnswerAudioRef, '/sounds/wrong-answer.mp3');
        if (audio) {
          audio.currentTime = 0;
          audio.play().catch(error => {
            console.error('Error playing wrong answer sound:', error);
          });
        }
      }
    }
  }, [currentSet, set2IsFlipped, selectedAnswer, currentQuestion, audioEnabled]);

  // Get translations for current language
  const languageText = React.useMemo(() => ({
    title: t('games.trivia.title') || 'Trivia',
    loading: t('games.trivia.loading') || 'Loading question...'
  }), [t, currentLanguage, i18n.language, ready]);

  // CDN Stats overlay - show only in development and localhost
  const showDebugInfo = stats && (process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost');

  // Colorful front card - button-19 style with Trivia.png image (3D effect, no white shine)
  const ColorfulFront = (
    <div 
      onClick={handleCardClick}
      style={{
        width: '100%',
        height: '100%',
        background: '#0053d0',
        border: 0,
        borderRadius: '0.375em',
        boxShadow: '0.2em 0.2em 0.5em rgba(0, 0, 0, 0.47), 0 -0.1em 0 0.1em rgba(0, 0, 0, 0.27), 0 0.1em 0 0.1em rgba(255, 255, 255, 0.27), -0.2em 0 0.2em #003f9d inset, 0 0.2em 0.2em rgba(255, 255, 255, 0.27) inset, 0.2em 0 0.2em rgba(255, 255, 255, 0.27) inset, 0 -0.2em 0.2em #003f9d inset',
        color: '#fff',
        cursor: 'pointer',
        fontSize: Math.max(fontSize * 1.2, 18),
        margin: 0,
        padding: '0.75em 1em',
        textShadow: '0 0 0.2em rgba(255, 255, 255, 0.47)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 'bold',
        position: 'relative',
        boxSizing: 'border-box'
      }}
    >
      <div style={{
        position: 'absolute',
        top: '8px',
        left: '8px',
        right: '8px',
        bottom: '8px',
        border: '2px solid rgba(255, 255, 255, 0.3)',
        borderRadius: '0.375em',
        pointerEvents: 'none'
      }}></div>
              <div style={{
          textAlign: 'center',
          zIndex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '0px',
          height: '100%',
          justifyContent: 'center'
        }}>
        {/* Winner enclosed in cups */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: Math.max(fontSize * 2.0, 30),
          fontWeight: 'bold',
          color: '#FFD700',
          textShadow: '0 0 10px rgba(255, 215, 0, 0.8), 0 0 20px rgba(255, 215, 0, 0.6), 0 0 30px rgba(255, 215, 0, 0.4)',
          filter: 'drop-shadow(2px 2px 4px rgba(0,0,0,0.3))',
          marginBottom: '8px'
        }}>
          <span style={{fontSize: Math.max(fontSize * 1.5, 22.5)}}>🏆</span>
          <span>{winnerName}</span>
          <span style={{fontSize: Math.max(fontSize * 1.5, 22.5)}}>🏆</span>
        </div>

        {/* Trivia.png image with fallback to brain emoji */}
        <div style={{
          width: Math.max(fontSize * 3.75, 60),
          height: Math.max(fontSize * 3.75, 60),
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          filter: 'drop-shadow(1px 1px 2px rgba(0,0,0,0.3))'
        }}>
                     <img 
             src="https://spinnergames.fun/images/Trivia.png" 
             alt="Trivia"
             loading="lazy"
             style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              filter: 'drop-shadow(0 0 4px rgba(255,255,255,0.8), 0 0 8px rgba(255,192,203,0.6), 0 0 12px rgba(255,182,193,0.4))'
            }}
            onError={(e) => {
              // Fallback to brain emoji if image fails to load
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
              const fallback = target.nextElementSibling as HTMLElement;
              if (fallback) {
                fallback.style.display = 'block';
              }
            }}
          />
          <div style={{
            fontSize: Math.max(fontSize * 2.5, 40),
            display: 'none',
            textShadow: '0 0 4px rgba(255,255,255,0.8), 0 0 8px rgba(255,192,203,0.6), 0 0 12px rgba(255,182,193,0.4)',
            filter: 'drop-shadow(1px 1px 2px rgba(0,0,0,0.3))'
          }}>🧠</div>
        </div>
        
        {/* Trivia title with custom black spades and white shine */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: Math.max(fontSize * 1.6, 24),
          fontWeight: 'bold'
        }}>
          <span style={{
            color: '#FFFFFF',
            fontSize: Math.max(fontSize * 1.8, 24),
            textShadow: '0 0 4px rgba(255,255,255,0.9), 0 0 8px rgba(255,255,255,0.7), 0 0 12px rgba(255,255,255,0.5)',
            filter: 'drop-shadow(1px 1px 2px rgba(0,0,0,0.5))'
          }}>♦</span>
          <span>{t('sidebar.games.trivia') || 'Trivia'}</span>
          <span style={{
            color: '#FFFFFF',
            fontSize: Math.max(fontSize * 1.8, 24),
            textShadow: '0 0 4px rgba(255,255,255,0.9), 0 0 8px rgba(255,255,255,0.7), 0 0 12px rgba(255,255,255,0.5)',
            filter: 'drop-shadow(1px 1px 2px rgba(0,0,0,0.5))'
          }}>♦</span>
        </div>
      </div>
    </div>
  );

  // Question card with 3-division layout: 20% (Question) + 60% (Options) + 20% (Submit/Skip)
  const QuestionCard = (
    <div style={{
      width: '100%',
      height: '100%',
              background: '#5c8bf0',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'stretch',
      justifyContent: 'flex-start',
      fontWeight: 'bold',
      fontSize: Math.max(fontSize * 0.8, 14),
      color: '#333',
      borderRadius: '20px',
      position: 'relative',
      boxSizing: 'border-box',
      border: '3px solid #1a237e',
      boxShadow: '0 8px 32px 0 rgba(26,35,126,0.10), 0 2px 8px 0 rgba(0,0,0,0.08)',
      minHeight: 220,
      overflow: 'hidden',
    }}>
      {/* Question Section (Fixed 20% of card height) */}
      <div style={{
        height: '20%',
        minHeight: windowWidth <= 768 ? '50px' : '60px',
        maxHeight: windowWidth <= 768 ? '60px' : '80px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxSizing: 'border-box',
        padding: windowWidth <= 768 ? '6px 10px' : '8px 12px',
        background: '#0d358c',
        color: 'white',
        textAlign: 'center',
        fontWeight: 600,
        fontSize: `clamp(13px, ${fontSize * 0.9}px, 1.6vw)`,
        lineHeight: 1.2,
        WebkitLineClamp: 3,
        overflow: 'hidden',
      }}>
        {currentQuestion?.question || (languageText.loading || 'Loading question...')}
      </div>

      {/* Options Section (Fixed 60% of card height) */}
      <div style={{
        height: '60%',
        minHeight: windowWidth <= 768 ? '100px' : '120px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        justifyContent: 'space-evenly',
        boxSizing: 'border-box',
        padding: windowWidth <= 768 ? '6px 10px' : '8px 12px',
        gap: windowWidth <= 768 ? '4px' : '6px',
        overflow: 'hidden',
        background: '#d1defa',
      }}>
        {currentQuestion && [
          { key: 'A', text: currentQuestion.A },
          { key: 'B', text: currentQuestion.B },
          { key: 'C', text: currentQuestion.C },
          { key: 'D', text: currentQuestion.D }
        ].map(({ key, text }) => (
          <button
            key={key}
            onClick={(e) => {
              e.stopPropagation();
              setSelectedAnswer(key);
            }}
            style={{
              flex: '1 1 0',
              minHeight: windowWidth <= 768 ? '24px' : '30px',
              maxHeight: '28%',
              width: '100%',
              boxSizing: 'border-box',
              background: selectedAnswer === key ? '#3b82f6' : 'white',
              border: selectedAnswer === key ? '2px solid #2563eb' : '2px solid rgba(26, 35, 126, 0.2)',
              borderRadius: '8px',
              color: selectedAnswer === key ? 'white' : '#000080',
              fontSize: `clamp(12px, ${fontSize * 0.8}px, 1.4vw)`,
              fontWeight: 600,
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              display: 'flex',
              alignItems: 'center',
              gap: windowWidth <= 768 ? '2px' : '4px',
              padding: windowWidth <= 768 ? '1px 4px 1px 6px' : '2px 6px 2px 8px',
              transition: 'all 0.15s',
              outline: 'none',
              overflow: 'hidden',
            }}
          >
            <span style={{
              fontWeight: 600,
              minWidth: windowWidth <= 768 ? '12px' : '14px',
              fontSize: `clamp(12px, ${fontSize * 0.8}px, 1.4vw)`,
              flexShrink: 0
            }}>
              {key}.
            </span>
            <span style={{ 
              flex: '1',
              textAlign: 'left',
              whiteSpace: 'normal',
              wordWrap: 'break-word',
              overflowWrap: 'break-word',
              overflow: 'hidden',
              lineHeight: '1.2',
              fontSize: `clamp(12px, ${fontSize * 0.8}px, 1.4vw)`,
            }}>
              {text || `No text for option ${key}`}
            </span>
          </button>
        ))}
      </div>

      {/* Submit/Skip Section (Fixed 20% of card height) */}
      <div style={{
        height: '20%',
        minHeight: windowWidth <= 768 ? '40px' : '50px',
        maxHeight: windowWidth <= 768 ? '50px' : '60px',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: windowWidth <= 768 ? '6px 10px' : '8px 12px',
        boxSizing: 'border-box',
        borderTop: '1.5px solid #e5e7eb',
        background: 'rgba(255,255,255,0.98)',
      }}>
        {/* Submit Button */}
        <button
          onClick={() => {
            if (!selectedAnswer) {
              const audio = initializeAudio(notSelectedAudioRef, '/sounds/not-selected.mp3');
              if (audio && audioEnabled) {
                audio.currentTime = 0;
                audio.play().catch(error => {
                  console.error('Error playing not-selected sound:', error);
                });
              }
              return;
            }
            // Only flip if answer is selected
            if (currentSet === 2 && !set2IsFlipped) {
              setSet2IsFlipped(true);
            }
          }}
          style={{
            flex: '1',
            height: '36px',
            background: selectedAnswer ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' : '#d1d5db',
            border: selectedAnswer ? '2px solid #059669' : '2px solid #d1d5db',
            borderRadius: '8px',
            color: selectedAnswer ? 'white' : '#374151',
            fontSize: `clamp(12px, ${fontSize * 0.8}px, 1.4vw)`,
            fontWeight: 'bold',
            cursor: 'pointer',
            boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
            transition: 'all 0.2s ease',
            outline: 'none',
            marginRight: '6px',
          }}
          onMouseEnter={e => ((e.target as HTMLElement).style.transform = 'translateY(-2px)')}
          onMouseOut={e => ((e.target as HTMLElement).style.transform = 'translateY(0px)')}
        >
                      {t('games.trivia.submit') || 'Submit'}
        </button>
        
        {/* Skip Button */}
        <button
          onClick={() => {
            setIsFadingOut(true);
            setTimeout(() => {
                          if (onComplete) {
              onComplete('🔘'); // Grey dot for skip
            }
            }, 500);
          }}
          style={{
            background: 'none',
            border: 'none',
            color: '#2563eb',
            fontSize: `clamp(9px, ${fontSize * 0.7}px, 1.2vw)`,
            cursor: 'pointer',
            padding: '6px 8px',
            borderRadius: '6px',
            fontWeight: 600,
            transition: 'all 0.2s',
            display: 'flex',
            alignItems: 'center',
            gap: '3px',
          }}
                      title={t('common.skip') || 'Skip'}
        >
          <span style={{fontWeight:'bold',fontSize:'1.3em',verticalAlign:'middle'}}>⏭</span>
                      {t('common.skip') || 'Skip'}
        </button>
      </div>
    </div>
  );

  // Answer card with 4-division layout: 20% (Title) + 30% (Answer) + 40% (Fun Fact) + 20% (Action buttons)
  const AnswerCard = (
    <div style={{
      width: '100%',
      height: '100%',
      background: 'linear-gradient(135deg,rgb(136, 170, 243),rgb(83, 131, 226) 100%)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'stretch',
      justifyContent: 'flex-start',
      fontWeight: 'bold',
      fontSize: Math.max(fontSize * 1.2, 18),
      color: 'white',
      borderRadius: '20px',
      position: 'relative',
      boxSizing: 'border-box',
      border: '3px solid rgba(255, 255, 255, 0.3)',
      overflow: 'hidden',
    }}>
      {/* Title Section (Fixed 20% of card height) */}
      <div style={{
        height: '20%',
        minHeight: '40px',
        maxHeight: '60px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxSizing: 'border-box',
        padding: '8px 12px',
        background: 'rgba(255, 255, 255, 0.1)',
        color: 'rgba(255, 255, 255, 0.9)',
        textAlign: 'center',
        fontWeight: 600,
        fontSize: `clamp(13px, ${fontSize * 1.0}px, 1.6vw)`,
        lineHeight: 1.2,
        textShadow: '1px 1px 2px rgba(0,0,0,0.2)',
        overflow: 'hidden',
      }}>
        {selectedAnswer && (
          isAnswerCorrect() ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ 
                background: '#10b981', 
                borderRadius: '50%', 
                padding: '4px', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)'
              }}>
                <Check size={20} color="white" style={{ filter: 'drop-shadow(1px 1px 2px rgba(0,0,0,0.3))' }} />
              </div>
              <span style={{ fontWeight: 'bold', textShadow: '2px 2px 4px rgba(0,0,0,0.5)' }}>{t('games.trivia.correctAnswer') || 'Correct Answer'}</span>
              <div style={{ 
                background: '#10b981', 
                borderRadius: '50%', 
                padding: '4px', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)'
              }}>
                <Check size={20} color="white" style={{ filter: 'drop-shadow(1px 1px 2px rgba(0,0,0,0.3))' }} />
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ 
                background: '#ef4444', 
                borderRadius: '50%', 
                padding: '4px', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                boxShadow: '0 2px 8px rgba(239, 68, 68, 0.3)'
              }}>
                <X size={20} color="white" style={{ filter: 'drop-shadow(1px 1px 2px rgba(0,0,0,0.3))' }} />
              </div>
              <span style={{ fontWeight: 'bold', textShadow: '2px 2px 4px rgba(0,0,0,0.5)' }}>{t('games.trivia.correctAnswer') || 'Correct Answer'}</span>
              <div style={{ 
                background: '#ef4444', 
                borderRadius: '50%', 
                padding: '4px', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                boxShadow: '0 2px 8px rgba(239, 68, 68, 0.3)'
              }}>
                <X size={20} color="white" style={{ filter: 'drop-shadow(1px 1px 2px rgba(0,0,0,0.3))' }} />
              </div>
            </div>
          )
        )}
        {!selectedAnswer && (
          <span style={{ fontWeight: 'bold', textShadow: '2px 2px 4px rgba(0,0,0,0.5)' }}>{t('games.trivia.correctAnswer') || 'Correct Answer'}</span>
        )}
      </div>

      {/* Middle Content Section (70% of card height containing Answer + Fun Fact) */}
      <div style={{
        height: '70%',
        minHeight: '120px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxSizing: 'border-box',
        padding: '8px 12px',
        gap: '8px',
        overflow: 'hidden',
      }}>
        {/* Answer Section (30% of middle section) */}
        <div style={{
          flex: '3 1 0%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
          padding: '8px 12px',
          background: 'linear-gradient(135deg,rgb(81, 135, 252) 0%,rgb(113, 142, 240) 100%)',
          borderRadius: '12px',
          border: '2px solid rgba(255, 255, 255, 0.2)',
          boxSizing: 'border-box',
        }}>
          <div style={{
            height: '100%',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: `clamp(16px, ${fontSize * 1.4}px, 2.4vw)`,
            fontWeight: 'bold',
            textAlign: 'center',
            color: '#fbbf24',
            textShadow: '3px 3px 6px rgba(0,0,0,0.5), 0 0 15px rgba(251, 191, 36, 0.6), 0 0 25px rgba(251, 191, 36, 0.4), 0 0 35px rgba(251, 191, 36, 0.2)',
            lineHeight: '1.2',
            wordWrap: 'break-word',
            overflowWrap: 'break-word',
            overflow: 'hidden',
            whiteSpace: 'normal',
            padding: '4px',
          }}>
            {currentQuestion?.Answer || 'Answer'}
          </div>
        </div>

        {/* Fun Fact Section (40% of middle section, bigger font) */}
        <div style={{
          flex: '4 1 0%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
          padding: '8px 12px',
          background: 'rgba(81, 135, 252, 0.4)',
          borderRadius: '8px',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxSizing: 'border-box',
        }}>
          <div style={{
            height: '100%',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: windowWidth <= 768 ? `clamp(14px, ${fontSize * 1.2}px, 2.0vw)` : `clamp(12px, ${fontSize * 1.0}px, 1.8vw)`,
            fontWeight: 'normal',
            color: '#e0f7fa',
            textAlign: 'center',
            textShadow: '1px 1px 2px rgba(0,0,0,0.4), 0 0 4px rgba(0,0,0,0.2)',
            lineHeight: '1.4',
            wordWrap: 'break-word',
            overflowWrap: 'break-word',
            overflow: 'hidden',
            whiteSpace: 'normal',
            padding: '4px',
          }}>
            {currentQuestion?.funFact}
          </div>
        </div>
      </div>

      {/* Action Buttons Section (Fixed 20% of card height) */}
      <div style={{
        height: '20%',
        minHeight: '40px',
        maxHeight: '60px',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '8px 16px',
        boxSizing: 'border-box',
        borderTop: '1px solid rgba(255,255,255,0.15)',
        background: 'rgba(255, 255, 255, 0.05)',
      }}>
        {/* Left: Slim Report button */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
        }}>
          <button
            onClick={handleReportClick}
            style={{
              background: '#e5e7eb',
              border: '1px solid #9ca3af',
              borderRadius: '4px',
              color: '#374151',
              fontSize: '0.75rem',
              cursor: 'pointer',
              outline: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '3px',
              transition: 'all 0.2s',
              padding: '3px 6px',
              minWidth: '40px',
              minHeight: '20px',
            }}
            title={t('common.report') || 'Report'}
          >
            <Flag 
              size={12} 
              style={{ 
                fill: selectedAction === 'report' ? '#dc2626' : 'none',
                stroke: selectedAction === 'report' ? '#dc2626' : '#374151'
              }} 
            />
            <span style={{ fontWeight: '400', fontSize: '0.7rem' }}>
              {t('common.report') || 'Report'}
            </span>
          </button>
        </div>

        {/* Right: Close button */}
        <button
          onClick={() => {
            setIsFadingOut(true);
            setTimeout(() => {
              if (onComplete) {
                // Determine outcome based on whether user answered correctly (if they selected an answer)
                const gameResultCandidate = selectedAnswer ? (isAnswerCorrect() ? '🟢' : '🔴') : undefined;
                onComplete(gameResultCandidate);
              }
            }, 500);
          }}
          style={{
            background: '#0d358c',
            border: '1px solid rgba(255, 255, 255, 0.5)',
            borderRadius: '8px',
            color: '#fff',
            fontSize: `clamp(12px, ${fontSize * 0.9}px, 1.6vw)`,
            fontWeight: 'bold',
            cursor: 'pointer',
            outline: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s',
            padding: '8px 12px',
            minWidth: '60px',
            minHeight: '32px',
            textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
            boxShadow: '0 2px 8px rgba(15, 118, 110, 0.3)',
          }}
          onMouseEnter={e => ((e.target as HTMLElement).style.transform = 'translateY(-1px)')}
          onMouseOut={e => ((e.target as HTMLElement).style.transform = 'translateY(0px)')}
                      title={t('games.trivia.close') || 'Close'}
        >
                      {t('games.trivia.close') || 'Close'}
        </button>
      </div>
      

    </div>
  );

  if (!isVisible) return null;
  
  // Wait for translations to be ready
  if (!ready) {
    return (
      <div style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 1000,
        color: 'white',
        fontSize: '16px'
      }}>
        Loading translations...
      </div>
    );
  }

  return (
    <>
      {/* Modal overlay to block background interaction */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0,0,0,0.3)',
          zIndex: 999,
          pointerEvents: 'auto',
          transition: 'opacity 0.3s ease-out',
          opacity: isFadingOut ? 0 : 1,
        }}
      />
      
      <div style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: cardWidth,
        height: cardHeight,
        minWidth: 180,
        minHeight: 180,
        maxWidth: '95vw',
        maxHeight: '95vh',
        zIndex: 1000,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
      }}
      className={isFadingIn ? 'card-fade-in' : isFadingOut ? 'card-fade-out' : ''}
      >
        <style>
          {`
            @keyframes fadeIn {
              from {
                opacity: 0;
                transform: translate(-50%, -50%) scale(0.8);
              }
              to {
                opacity: 1;
                transform: translate(-50%, -50%) scale(1);
              }
            }
            
            @keyframes fadeOut {
              from {
                opacity: 1;
                transform: translate(-50%, -50%) scale(1);
              }
              to {
                opacity: 0;
                transform: translate(-50%, -50%) scale(0.8);
              }
            }
            
            .card-fade-in {
              animation: fadeIn 0.3s ease-out forwards;
            }
            
            .card-fade-out {
              animation: fadeOut 0.5s ease-in forwards;
            }
          `}
        </style>
        
        {/* First card set: Colorful -> Question */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          opacity: currentSet === 1 ? 1 : 0,
          visibility: currentSet === 1 ? 'visible' : 'hidden',
          transition: 'opacity 0.3s ease'
        }}>
          <FlashCard 
            front={ColorfulFront} 
            back={QuestionCard}
            isFlipped={isFlipped}
            onFlip={handleCardClick}
            width={cardWidth}
            height={cardHeight}
          />
        </div>

        {/* Second card set: Question -> Answer */}
        <div style={{ 
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          opacity: currentSet === 2 ? 1 : 0,
          visibility: currentSet === 2 ? 'visible' : 'hidden',
          transition: 'none' // No transition to avoid any visual flips
        }}>
          <FlashCard 
            front={QuestionCard} 
            back={AnswerCard}
            isFlipped={set2IsFlipped}
            onFlip={() => {}}
            width={cardWidth}
            height={cardHeight}
          />
        </div>
      </div>

      {/* Report Modal */}
      <ReportModal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        gameType="trivia"
        questionId={currentQuestion?.id}
        questionText={currentQuestion ? `${currentQuestion.question} - A: ${currentQuestion.A}, B: ${currentQuestion.B}, C: ${currentQuestion.C}, D: ${currentQuestion.D}` : ''}
        onReportSubmitted={handleReportSubmitted}
      />

      {/* CDN Stats Overlay */}
      {showDebugInfo && (
        <div style={{
          position: 'fixed',
          top: '64px', // 54px header height + 10px margin
          right: '10px',
          background: 'rgba(0, 0, 0, 0.8)',
          color: 'white',
          padding: '8px 12px',
          borderRadius: '6px',
          fontSize: '12px',
          fontFamily: 'monospace',
          zIndex: 2000,
          maxWidth: '300px',
          wordBreak: 'break-word'
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>CDN Stats:</div>
          <div>Current Questions: {stats.currentQuestions}</div>
          <div>Used Questions: {stats.usedQuestions}</div>
          <div>Prefetch Block: {stats.nextPrefetchBlock ?? '-'}</div>
          <div>Current Block: {stats.currentBlock}</div>
          <div>Total Blocks: {stats.totalBlocks}</div>
          {questionError && (
            <div style={{ color: '#ff6b6b', marginTop: '4px' }}>
              Error: {questionError}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default TriviaGameCard; 